import React, { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useWebSocket } from '../contexts/WebSocketContext'
import { apiUrl } from '../config/api'
import SpectatorGameView from '../components/SpectatorGameView/SpectatorGameView'
import UserDisplay from '../components/UserDisplay/UserDisplay'
import ChessBoard from '../components/Chess/ChessBoard'
import './SpectateGamesList.css'

const SpectateGamesList = () => {
  const { session } = useAuth()
  const { isConnected, spectateGame, leaveSpectate, addMessageHandler, removeMessageHandler } = useWebSocket()
  const [games, setGames] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedGameId, setSelectedGameId] = useState(null)
  const [activePreviewId, setActivePreviewId] = useState(null)
  const [currentTime, setCurrentTime] = useState(Date.now())

  // Debug State
  useEffect(() => {
     console.log(`[SpectateState] Games: ${games.length}, ActivePreview: ${activePreviewId}, Connected: ${isConnected}, Token: ${!!session?.access_token}`)
     if (games.length > 0) {
         console.log(`[SpectateState] First Game ID: ${games[0].id}`)
     }
  }, [games.length, activePreviewId, isConnected, session])

  // Real-time timer countdown - tick every 100ms
  useEffect(() => {
    const interval = setInterval(() => {
        setCurrentTime(Date.now())
    }, 100)
    return () => clearInterval(interval)
  }, [])

  // Check URL for game ID on component mount
  useEffect(() => {
    const path = window.location.pathname
    const match = path.match(/^\/spectate\/([a-f0-9-]+)$/)
    if (match) {
      const gameIdFromUrl = match[1]
      setSelectedGameId(gameIdFromUrl)
    }
  }, [])

  const fetchSpectableGames = useCallback(async (isSilent = false) => {
    try {
      if (!isSilent) setLoading(true)
      setError(null)

      if (!session?.access_token) return

      const response = await fetch(apiUrl('games/spectatable'), {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      const rawGames = data.games || []
      
      setGames(currentGames => {
          // Calculate merged state synchronously using current 'games' from scope
          const gamesWithTimestamp = rawGames.map(g => {
              const gameId = String(g.id)
              const existingGame = currentGames.find(cg => cg.id === gameId)
              
              if (existingGame && existingGame.id === activePreviewId) {
                  return {
                      ...g,
                      id: gameId,
                      // Preserve real-time state from WS if available
                      board: existingGame.board, 
                      current_board: existingGame.current_board || g.current_board, 
                      current_turn: existingGame.current_turn, 
                      last_move: existingGame.last_move,
                      white_time_remaining: existingGame.white_time_remaining, 
                      black_time_remaining: existingGame.black_time_remaining,
                      last_update_local_time: existingGame.last_update_local_time, 
                  }
              }

              // Normal clean fresh state
              return {
                  ...g,
                  id: gameId,
                  white_time_remaining: typeof g.white_time_remaining !== 'undefined' ? Number(g.white_time_remaining) : (Number(g.white_time) || 0),
                  black_time_remaining: typeof g.black_time_remaining !== 'undefined' ? Number(g.black_time_remaining) : (Number(g.black_time) || 0),
                  last_update_local_time: Date.now()
              }
          })
          return gamesWithTimestamp
      })

      setLoading(false)

    } catch (err) {
      console.error('Error fetching spectatable games:', err)
      setError('Failed to load spectatable games.')
      setLoading(false)
    }
  }, [session?.access_token, activePreviewId]) // Removed 'games' dependency

  // Ensure an active preview is selected when games loads
  useEffect(() => {
     if (games.length > 0) {
         if (!activePreviewId || !games.find(g => g.id === activePreviewId)) {
             setActivePreviewId(games[0].id)
         }
     } else if (games.length === 0 && activePreviewId) {
          setActivePreviewId(null)
     }
  }, [games, activePreviewId])

  useEffect(() => {
    if (session?.access_token) {
      fetchSpectableGames()
    }
  }, [session?.access_token, fetchSpectableGames])

  // Refresh games every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (session?.access_token && !selectedGameId) {
        fetchSpectableGames(true) // Silent refresh
      }
    }, 5000)

    return () => clearInterval(interval)
  }, [session?.access_token, selectedGameId, fetchSpectableGames])

  // Manage WebSocket subscription for the active preview

  useEffect(() => {
    if (!isConnected || !activePreviewId) return

    let isMounted = true
    
    // Subscribe to the featured game
    console.log(`[SpectateDebug] Subscribing to featured game: ${activePreviewId}`)
    spectateGame(activePreviewId).catch(err => {
        if (isMounted) console.error("Failed to subscribe to featured game:", err)
    })

    // Handler for move updates
    const handleMoveUpdate = (message) => {
        // console.log("[SpectateDebug] Move update received:", message)
        setGames(prevGames => {
            const gameExists = prevGames.find(g => g.id == message.game_id)
            if (!gameExists) return prevGames

            return prevGames.map(g => {
                if (g.id == message.game_id) {
                    return {
                        ...g,
                        board: message.current_board, 
                        current_board: message.current_board,
                        last_move: message.move,
                        move_count: message.move_count, // Critical for ChessBoard key/updates
                        current_turn: message.current_turn,
                        white_time_remaining: Number(message.white_time_remaining),
                        black_time_remaining: Number(message.black_time_remaining),
                        red_squares: message.red_squares,
                        blue_squares: message.blue_squares,
                        green_squares: message.green_squares,
                        visual_effects: message.visual_effects,
                        last_update_local_time: Date.now()
                    }
                }
                return g
            })
        })
    }

    const handleGameState = (message) => {
        setGames(prevGames => {
             const gameExists = prevGames.find(g => g.id == message.game_id)
             if (!gameExists) return prevGames
             
             const gameData = message.data?.game || message.data
             if (!gameData) return prevGames

             return prevGames.map(g => {
                 if (g.id == message.game_id) {
                     return { 
                         ...g, 
                         ...gameData,
                         white_time_remaining: typeof gameData.white_time_remaining !== 'undefined' ? Number(gameData.white_time_remaining) : (Number(gameData.white_time) || g.white_time_remaining),
                         black_time_remaining: typeof gameData.black_time_remaining !== 'undefined' ? Number(gameData.black_time_remaining) : (Number(gameData.black_time) || g.black_time_remaining),
                         last_update_local_time: Date.now()
                     }
                 }
                 return g
             })
        })
    }

    addMessageHandler('move_update', handleMoveUpdate)
    addMessageHandler('game_state', handleGameState)

    return () => {
        isMounted = false
        // Only unsubscribe if we are unmounting or changing IDs.
        // Critical: React cleanup runs BEFORE the next setup.
        // If we switch from Game A to Game B:
        // 1. Cleanup A runs -> calls leaveSpectate()
        // 2. Setup B runs -> calls spectateGame(B)
        // If the server processes events out of order (Join B, then Leave A), we might end up spectating nothing.
        // To fix this, we need to ensure 'leave' is specific or handled better.
        // But 'leaveSpectate' takes no args in valid implementation.
        // Best approach: A short delay or just firing it.
        // Ideally the backend handles 'leave_spectate' as "leave current room".
        // If we join B, we enter room B. "Leave" might leave room B if processed late.
        // FIX: Verify if we can just NOT leave when switching? 
        // No, we must leave to stop receiving events for A.
        leaveSpectate() 
        removeMessageHandler('move_update')
        removeMessageHandler('game_state')
    }
  }, [isConnected, activePreviewId, spectateGame, leaveSpectate, addMessageHandler, removeMessageHandler])


  const handleSpectateGame = (gameId) => {
    setSelectedGameId(gameId)
    window.history.pushState({}, '', `/spectate/${gameId}`)
  }

  const handleBackToList = () => {
    setSelectedGameId(null)
    window.history.pushState({}, '', '/spectate')
    fetchSpectableGames()
  }

  const formatTimeControl = (game) => {
    if (game.time_control && !isNaN(game.time_control)) {
      const minutes = Math.floor(game.time_control / 60)
      const increment = game.increment || 0
      return `${minutes}+${increment}`
    }
    return 'Standard'
  }

  const formatTime = (timeInSeconds, game, isPlayerWhite) => {
    const val = Number(timeInSeconds)
    if (isNaN(val)) {
      return '--:--'
    }

    // Interpolate time if game is active and it's this player's turn
    let adjustedTime = val
    
    // Check if we should interpolate
    if (game && game.status !== 'ended' && game.status !== 'checkmate' && game.status !== 'draw') {
         const isTurn = (game.current_turn === 'white' && isPlayerWhite) || 
                        (game.current_turn === 'black' && !isPlayerWhite)
         
         if (isTurn && game.last_update_local_time) {
             const elapsedSeconds = (currentTime - game.last_update_local_time) / 1000
             adjustedTime = Math.max(0, val - elapsedSeconds)
         }
    }

    const minutes = Math.floor(Math.abs(adjustedTime) / 60)
    const seconds = Math.floor(Math.abs(adjustedTime) % 60)

    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  const getGameTypeDisplay = (game) => {
    if (game.tournament_id) {
      return 'Tournament'
    }
    if (game.variant && game.variant !== 'standard') {
      return game.variant.charAt(0).toUpperCase() + game.variant.slice(1)
    }
    return 'Standard'
  }

  if (selectedGameId) {
    return (
      <SpectatorGameView
        gameId={selectedGameId}
        onBack={handleBackToList}
      />
    )
  }

  if (!session?.access_token) {
    return (
      <div className="spectate-games-list">
        <div className="auth-required">
          <h2>Sign in Required</h2>
          <p>You need to be signed in to spectate games.</p>
        </div>
      </div>
    )
  }

  const featuredGame = games.find(g => g.id === activePreviewId)

  return (
    <div className="spectate-games-list">
      <div className="page-header">
        <h1>Spectate Live Games</h1>
      </div>

      {loading && games.length === 0 && (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading live games...</p>
        </div>
      )}

      {error && (
        <div className="error-state">
          <div className="error-message">{error}</div>
          <button onClick={fetchSpectableGames} className="retry-button">
            Try Again
          </button>
        </div>
      )}

      {!loading && !error && (
        <>
            {/* Featured Section - Shows either Active Game or Empty State Placeholder */}
            <div className="featured-board-section">
                <div className="featured-board-wrapper">
                      {/* Top Player Bar (Hidden if no game, or placeholder) */}
                      {games.length > 0 && featuredGame ? (
                         <div className="player-bar top-player">
                            <div className="player-bar-left">
                                <span className="player-rating">({featuredGame.black_player?.rating || '?'})</span>
                                <UserDisplay
                                    user={featuredGame.black_player}
                                    color="black"
                                    showStatusBadges={true}
                                    showTitleBadges={true}
                                />
                            </div>
                            <div className={`player-clock ${featuredGame.current_turn === 'black' ? 'active' : ''}`}>
                                {formatTime(featuredGame.black_time_remaining, featuredGame, false)}
                            </div>
                         </div>
                      ) : (
                         /* Empty state top bar placeholder to maintain shape, or just hide it? 
                            User said "inside the chess board preview", implying the board is the main thing. 
                            Let's keep the wrapper structure but maybe empty bars or just the board.
                            To look "exactly like dashboard", it usually has bars. 
                            But with no players, bars look broken. 
                            Let's render "waiting" placeholders or just hide bars for empty state 
                            and only show the board with the message. 
                         */
                         <div className="player-bar top-player" style={{visibility: 'hidden'}}>
                            {/* Invisible bar to maintain spacing/layout if desired, or simpler: don't render bars */}
                         </div>
                      )}

                      {/* The Board */}
                      <div className="featured-chess-board" onClick={() => featuredGame && handleSpectateGame(featuredGame.id)}>
                         <ChessBoard
                             key={featuredGame ? `${featuredGame.id}-${featuredGame.move_count || 0}` : 'empty'}
                             game={featuredGame ? {
                                ...featuredGame,
                                board: featuredGame.current_board || featuredGame.board
                             } : {
                                fen: 'start', // Render start position if no game
                                board: null   // specific internal prop might differ, but usually 'fen' or default works
                             }}
                             onSquareClick={() => {}}
                             selectedSquare={null}
                             legalMoves={[]}
                             lastMove={featuredGame?.last_move}
                             isPlayerWhite={true}
                             disabled={true}
                             allowResize={false}
                         />
                          
                          {/* Overlay: Click to Spectate (if game exists) OR No Live Games (if empty) */}
                          {games.length > 0 ? (
                              <div className="board-click-zone" />
                          ) : (
                              <div className="board-empty-overlay">
                                  <h3>No Live Games</h3>
                                  <p>There are no active games available for spectating at the moment.</p>
                              </div>
                          )}
                      </div>

                      {/* Bottom Player Bar */}
                      {games.length > 0 && featuredGame ? (
                         <div className="player-bar bottom-player">
                            <div className="player-bar-left">
                                <span className="player-rating">({featuredGame.white_player?.rating || '?'})</span>
                                <UserDisplay
                                    user={featuredGame.white_player}
                                    color="white"
                                    showStatusBadges={true}
                                    showTitleBadges={true}
                                />
                            </div>
                            <div className={`player-clock ${featuredGame.current_turn === 'white' ? 'active' : ''}`}>
                                {formatTime(featuredGame.white_time_remaining, featuredGame, true)}
                            </div>
                         </div>
                      ) : (
                         <div className="player-bar bottom-player" style={{visibility: 'hidden'}}>
                         </div>
                      )}
                </div>
            </div>

            {/* Other Games Grid */}
            {games.length > 0 && (
                <div className="games-grid">
                {games.map((game) => (
                    <div key={game.id} className="spectate-game-card">
                    <div className="game-header">
                        <div className="game-type">{getGameTypeDisplay(game)}</div>
                        <div className="time-control">{formatTimeControl(game)}</div>
                    </div>

                    <div className="players-section">
                        <div className="player-info">
                        <UserDisplay
                            user={game.white_player}
                            color="white"
                            time={formatTime(game.white_time_remaining, game, true)}
                            isCurrentTurn={game.current_turn === 'white'}
                            showTime={true}
                            compact={true}
                        />
                        </div>

                        <div className="vs-indicator">VS</div>

                        <div className="player-info">
                        <UserDisplay
                            user={game.black_player}
                            color="black"
                            time={formatTime(game.black_time_remaining, game, false)}
                            isCurrentTurn={game.current_turn === 'black'}
                            showTime={true}
                            compact={true}
                        />
                        </div>
                    </div>

                    <button
                        onClick={() => handleSpectateGame(game.id)}
                        className="spectate-button"
                    >
                        Spectate
                    </button>
                    </div>
                ))}
                </div>
            )}
        </>
      )}
    </div>
  )
}

export default SpectateGamesList